<script>
  import { ResizableHandle, ResizablePane, ResizablePaneGroup } from '@sveltia/ui';
  import { IndexedDB } from '@sveltia/utils/storage';
  import { get } from 'svelte/store';

  import { hasOverlay } from '$lib/services/app/navigation';
  import { backend } from '$lib/services/backends';
  import { env } from '$lib/services/user/env.svelte';

  /**
   * @import { Snippet } from 'svelte';
   */

  /**
   * @typedef {object} Props
   * @property {string} [class] CSS class name on the button.
   * @property {string} [uiSettingsKey] Key for storing UI settings in IndexedDB. If not provided,
   * the sidebar width will not be persisted.
   * @property {Snippet} [primarySidebar] Primary sidebar content.
   * @property {Snippet} [main] Main content.
   */

  /** @type {Props & Record<string, any>} */
  let {
    /* eslint-disable prefer-const */
    class: className = '',
    uiSettingsKey = undefined,
    primarySidebar = undefined,
    main = undefined,
    ...rest
    /* eslint-enable prefer-const */
  } = $props();

  /** @type {HTMLElement | undefined} */
  let container = $state();
  /** @type {number | undefined} */
  let sidebarWidth = $state();

  /** @type {IndexedDB | null} */
  let uiSettingsDB = null;

  /**
   * Restore the sidebar width from IndexedDB, or use the default width if not set.
   */
  const restoreSidebarWidth = async () => {
    if (!uiSettingsKey) return;

    const { databaseName } = get(backend)?.repository ?? {};

    uiSettingsDB = databaseName ? new IndexedDB(databaseName, 'ui-settings') : null;
    sidebarWidth = (await uiSettingsDB?.get(uiSettingsKey))?.sidebarWidth ?? 240;
  };

  /**
   * Save the sidebar width to IndexedDB.
   * @param {number} percent Sidebar width as a percentage of the container’s width.
   */
  const saveSidebarWidth = async (percent) => {
    if (!uiSettingsDB || !uiSettingsKey || !container) return;

    await uiSettingsDB.set(uiSettingsKey, {
      ...(await uiSettingsDB.get(uiSettingsKey)),
      sidebarWidth: Math.round(container.clientWidth * (percent / 100)),
    });
  };

  $effect.pre(() => {
    void [$hasOverlay];

    // `ResizablePaneGroup` doesn’t work well when the container is inert, so we need to wait until
    // the overlay is actually gone before restoring the sidebar width.
    window.requestAnimationFrame(() => {
      if (!$hasOverlay) {
        restoreSidebarWidth();
      }
    });
  });
</script>

<div
  role="group"
  id="page-container"
  class="outer {className}"
  inert={$hasOverlay}
  {...rest}
  bind:this={container}
>
  {#if env.isSmallScreen || !primarySidebar || !main}
    {@render primarySidebar?.()}
    {@render main?.()}
  {:else if sidebarWidth !== undefined}
    <ResizablePaneGroup
      onResize={({ sizes }) => {
        saveSidebarWidth(sizes[0]);
      }}
    >
      <ResizablePane defaultSize="{sidebarWidth}px" minSize="160px" maxSize="480px">
        {@render primarySidebar()}
      </ResizablePane>
      <ResizableHandle />
      <ResizablePane>
        {@render main()}
      </ResizablePane>
    </ResizablePaneGroup>
  {/if}
</div>

<style>
  .outer {
    flex: auto;
    display: flex;
    overflow: hidden;

    &[inert] {
      display: none;
    }

    :global {
      .primary-sidebar {
        display: flex;
        flex-direction: column;
        flex: none;
        overflow-y: auto;
        height: 100%;

        @media (width < 768px) {
          flex: auto;
          width: auto;
          background-color: var(--sui-primary-background-color);
        }

        /* Mobile header */
        header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 10px;
          height: var(--sui-primary-toolbar-size);

          h2 {
            padding: 0 10px;
            font-size: var(--sui-font-size-x-large);
          }
        }

        .sui.search-bar {
          margin-inline: 12px;
          --sui-textbox-background-color: var(--sui-tertiary-background-color);
        }

        [role='radiogroup'] {
          width: 100%;
        }

        [role='listbox'] {
          margin: 8px;
          border-width: 0;
          background-color: transparent;

          button {
            display: flex;
            justify-content: flex-start;
            border-radius: var(--sui-control-medium-border-radius);
            width: 100%;
            text-align: start;

            @media (pointer: coarse) {
              height: 48px;
            }

            &:not(:first-child) {
              margin-top: 4px;
            }

            &:not(:focus) {
              border-color: transparent;
            }

            span {
              flex: none;
            }

            .label {
              flex: auto;
              overflow: hidden;
            }

            .icon {
              transition: color 200ms;
            }

            .icon.check {
              display: none;
            }

            .count {
              padding: 2px;
              color: var(--sui-tertiary-foreground-color);
              font-size: var(--sui-font-size-small);
              transition: color 200ms;
            }
          }

          [role='option'][aria-selected='true'] {
            color: var(--sui-highlight-foreground-color);
            background-color: var(--sui-selected-background-color);

            .count {
              color: var(--sui-highlighted-foreground-color);
            }
          }

          [role='option'].dragover {
            color: var(--sui-primary-accent-color-inverted) !important;
            background-color: var(--sui-primary-accent-color) !important;
          }
        }

        .sui.divider {
          margin: 8px 0;
        }
      }
    }
  }
</style>
